import { useEffect, useCallback } from 'react';
import { useRoutingStore } from '@/store/routingStore';
import { supabase } from '@/lib/supabase/client';
import { 
  Routing, RoutingCreate, RoutingFilter, RoutingOperation, 
  RoutingOperationCreate, RoutingStatus, RoutingFormData,
  RoutingComponentOperationCreate, RoutingComponentOperationWithDetails,
  ComponentWithOperations
} from '@/types/routing';
import { UUID } from '@/types/common';

export const useRoutings = () => {
  const {
    routings,
    currentRouting,
    routingOperations,
    componentOperations,
    isLoading,
    error,
    fetchRoutings,
    getRoutingById,
    createRouting,
    updateRouting,
    updateRoutingStatus,
    deleteRouting,
    duplicateRouting,
    fetchRoutingOperations,
    createRoutingOperation,
    updateRoutingOperation,
    deleteRoutingOperation,
    reorderRoutingOperations,
    fetchComponentOperationsByRouting,
    createComponentOperation,
    deleteComponentOperation,
    getOperationsGroupedByComponent,
    clearCurrentRouting,
    setError,
  } = useRoutingStore();

  // One-time initialization - only fetch on first mount
  useEffect(() => {
    // Don't reload if we already have data
    if (routings.length > 0) return;
    
    // Track if component is mounted to avoid state updates after unmount
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const loadData = async () => {
      try {
        console.log('useRoutings: Initial data fetch triggered');
        
        // Set a hard timeout to ensure isLoading gets reset even if fetch fails
        timeoutId = setTimeout(() => {
          if (isMounted) {
            console.log('useRoutings: Timeout reached, resetting loading state');
            setError('Timeout loading routings. Please try again.');
          }
        }, 5000);
        
        // We'll call fetchRoutings directly here instead of the store version
        // to avoid dependency cycle
        if (isMounted) {
          await fetchRoutings();
        }
        
        if (isMounted) {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        if (isMounted) {
          clearTimeout(timeoutId);
          console.error('Error loading routings:', error);
          setError((error as Error).message);
        }
      }
    };
    
    loadData();
    
    // Clear timeout on cleanup and prevent state updates after unmount
    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array means this only runs once on mount

  // Create a routing with operations and component-operations in a single transaction
  const createRoutingWithOperations = useCallback(async (data: RoutingFormData, userId: UUID): Promise<UUID | null> => {
    try {
      // First create the routing
      const routingData = { ...data.routingInfo, created_by: userId };
      const routingId = await createRouting(routingData);
      
      if (!routingId) {
        throw new Error('Failed to create routing');
      }
      
      // Then create operations if any
      const operationIdMap = new Map<number, UUID>(); // Map to store temp ID to actual DB ID
      
      if (data.operations && data.operations.length > 0) {
        for (let i = 0; i < data.operations.length; i++) {
          const operation = { 
            ...data.operations[i], 
            routing_id: routingId, 
            created_by: userId 
          };
          const opId = await createRoutingOperation(operation);
          
          if (opId) {
            // Store the mapping of index to DB ID
            operationIdMap.set(i, opId);
          }
        }
      }
      
      // Create component operation mappings if any
      if (data.componentOperations && data.componentOperations.length > 0) {
        for (const componentOp of data.componentOperations) {
          // Replace temp operation ID with actual DB ID if needed
          const operationId = typeof componentOp.operation_id === 'number' 
            ? operationIdMap.get(componentOp.operation_id as number) 
            : componentOp.operation_id;
            
          if (!operationId) {
            console.error(`Operation ID not found for component operation`);
            continue;
          }
          
          await createComponentOperation({
            routing_id: routingId,
            component_id: componentOp.component_id,
            operation_id: operationId,
            created_by: userId
          });
        }
      }
      
      return routingId;
    } catch (error) {
      console.error('Error creating routing with operations:', error);
      setError((error as Error).message);
      return null;
    }
  }, [createRouting, createRoutingOperation, createComponentOperation, setError]);

  // Load routing details including operations and component operations
  const loadRoutingDetails = useCallback(async (routingId: UUID): Promise<boolean> => {
    try {
      const routing = await getRoutingById(routingId);
      
      if (routing) {
        await Promise.all([
          fetchRoutingOperations(routingId),
          fetchComponentOperationsByRouting(routingId)
        ]);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error loading routing details:', error);
      setError((error as Error).message);
      return false;
    }
  }, [getRoutingById, fetchRoutingOperations, fetchComponentOperationsByRouting, setError]);

  // Update routing with operations
  // Fetch operations for a specific component
  const fetchComponentOperations = useCallback(async (
    routingId: UUID, 
    componentId: UUID
  ): Promise<RoutingComponentOperationWithDetails[]> => {
    try {
      const { data, error } = await supabase
        .from('routing_component_operations_with_details')
        .select('*')
        .eq('routing_id', routingId)
        .eq('component_id', componentId)
        .order('sequence_no');
      
      if (error) {
        throw error;
      }
      
      return data || [];
    } catch (error) {
      console.error('Error fetching component operations:', error);
      setError((error as Error).message);
      return [];
    }
  }, [setError]);

  // Update routing with operations
  const updateRoutingWithOperations = useCallback(async (
    routingId: UUID, 
    updates: Partial<Routing>,
    operations: {
      create: RoutingOperationCreate[],
      update: { id: UUID, updates: Partial<RoutingOperation> }[],
      delete: UUID[]
    }
  ): Promise<boolean> => {
    try {
      // 1. Update the routing header
      if (Object.keys(updates).length > 0) {
        const success = await updateRouting(routingId, updates);
        if (!success) return false;
      }
      
      // 2. Delete operations
      for (const opId of operations.delete) {
        await deleteRoutingOperation(opId);
      }
      
      // 3. Update operations
      for (const op of operations.update) {
        await updateRoutingOperation(op.id, op.updates);
      }
      
      // 4. Create new operations
      for (const op of operations.create) {
        await createRoutingOperation({ ...op, routing_id: routingId });
      }
      
      return true;
    } catch (error) {
      console.error('Error updating routing with operations:', error);
      setError((error as Error).message);
      return false;
    }
  }, [updateRouting, deleteRoutingOperation, updateRoutingOperation, createRoutingOperation, setError]);

  return {
    // State
    routings,
    currentRouting,
    routingOperations,
    componentOperations,
    isLoading,
    error,
    
    // Basic operations
    fetchRoutings,
    getRoutingById,
    createRouting,
    updateRouting,
    updateRoutingStatus,
    deleteRouting,
    duplicateRouting,
    
    // Operation-level operations
    fetchRoutingOperations,
    createRoutingOperation,
    updateRoutingOperation,
    deleteRoutingOperation,
    reorderRoutingOperations,
    
    // Component operations
    fetchComponentOperationsByRouting,
    createComponentOperation,
    deleteComponentOperation,
    getOperationsGroupedByComponent,
    
    // State management
    clearCurrentRouting,
    setError,
    
    // Compound operations
    createRoutingWithOperations,
    loadRoutingDetails,
    updateRoutingWithOperations,
    fetchComponentOperations,
  };
};
